import { Form, Input, Select } from "antd";
import { useLazySearchCreditorsQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "common/utils";

export default function BaseStep({ initialCreditors }) {
  const [creditorSearch, setCreditorSearch] = useState("");

  const [onSearchCreditors, { data: searchedCreditors, isLoading: isCreditorSearchLoading }] =
    useLazySearchCreditorsQuery();

  // Debounce the search to avoid too many requests
  // This will wait for 500 ms after the last keystroke before making the API call
  const debouncedOnSearchCreditors = useMemo(() => {
    return debounce((query) => onSearchCreditors({ query }), 500);
  }, [onSearchCreditors]);

  useEffect(() => {
    if (!creditorSearch) return;
    debouncedOnSearchCreditors(creditorSearch);
  }, [creditorSearch, debouncedOnSearchCreditors]);

  const renderedCreditors = useMemo(() => {
    const creditors = [];

    if (initialCreditors?.length > 0) {
      creditors.push(...initialCreditors);
    }

    if (searchedCreditors?.length > 0) {
      creditors.push(...searchedCreditors);
    }

    // initialCreditors and searchedCreditors can have the same creditor, so we deduplicate them before returning
    return creditors.filter(
      (creditor, index, self) => index === self.findIndex((c) => c.id === creditor.id),
    );
  }, [initialCreditors, searchedCreditors]);

  return (
    <>
      <Form.Item name="id" hidden>
        <Input hidden />
      </Form.Item>
      <Form.Item
        name={["name"]}
        label="Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input placeholder="Enter name" />
      </Form.Item>
      <Form.Item
        name={["creditorIds"]}
        label="Clients"
        tooltip="This developer will only be able to access the selected clients. If no clients are selected, it will have access to all of them."
      >
        <Select
          mode="multiple"
          placeholder="Select clients"
          optionFilterProp="label"
          searchValue={creditorSearch}
          onSearch={setCreditorSearch}
          loading={isCreditorSearchLoading}
          notFoundContent="Start typing to search for clients"
          options={renderedCreditors.map((creditor) => ({
            label: `${creditor.name} (${creditor.code})`,
            value: creditor.id,
          }))}
        />
      </Form.Item>
    </>
  );
}
